import React from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';

import { SETTINGS_PATH_INTEGRATION_OMD } from '../../../../utils/settingsRoutes';
import { OmdMediaPreview, type OmdMediaPreviewState } from '../OmdMediaPreview';
import { type OmdPanelTabId } from '../../utils/omdProductView';
import { useOmdPanelSettings } from '../../utils/omdPanelSettings';
import {
  getCachedOmdProduct,
  getOmdProductCacheKey,
  getOrFetchOmdProduct,
} from '../../utils/omdProductCache';
import { OmdRawJsonSection } from './shared/OmdRawJsonSection';
import { OmdPanelFooter } from './OmdPanelFooter';
import { OmdPanelHeader, TAB_LABELS } from './OmdPanelHeader';
import { OmdImageLightbox } from './shared/OmdImageLightbox';
import { useEscapeClose, useFocusTrap } from './useFocusTrap';
import { isOmdCatalogMaterial, requireOmdProduct, resolveArticleNumber } from './omdPanelHelpers';
import type { OmdLightboxState, OmdPanelMaterial } from './types';
import { OmdOverviewTab, OmdFlowSection } from './tabs/OmdOverviewTab';
import { OmdTechnikTab } from './tabs/OmdTechnikTab';
import {
  buildLightboxPictures,
  docPreviewFromDocument,
  OmdDocumentsTab,
} from './tabs/OmdDocumentsTab';
import { OmdPreiseTab } from './tabs/OmdPreiseTab';
import type { OmdDocument } from '../../utils/omdProductView';
import { InlineLoadingState } from '../../../../components/LoadingSpinner';

export type { OmdPanelMaterial };

type OmdProductPanelProps = {
  material: OmdPanelMaterial | null;
  open: boolean;
  onClose: () => void;
  omdEnabledSlugs?: ReadonlySet<string>;
};

type FetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ineligible'; message: string }
  | { status: 'error'; message: string; code?: string; settingsLink?: boolean }
  | { status: 'success'; product: unknown };

export function OmdProductPanel({ material, open, onClose, omdEnabledSlugs }: OmdProductPanelProps) {
  const { instanceId } = useParams<{ instanceId: string }>();
  const settingsPath = instanceId ? `/i/${instanceId}${SETTINGS_PATH_INTEGRATION_OMD}` : SETTINGS_PATH_INTEGRATION_OMD;
  const [settings] = useOmdPanelSettings();
  const [fetchState, setFetchState] = React.useState<FetchState>({ status: 'idle' });
  const [activeTab, setActiveTab] = React.useState<OmdPanelTabId>('uebersicht');
  const [lightbox, setLightbox] = React.useState<OmdLightboxState>(null);
  const [docPreview, setDocPreview] = React.useState<OmdMediaPreviewState>(null);
  const panelRef = React.useRef<HTMLElement>(null);

  useFocusTrap(open, panelRef);
  useEscapeClose(open && !lightbox && !docPreview, onClose);

  React.useEffect(() => {
    if (!open || !material) {
      setFetchState({ status: 'idle' });
      setLightbox(null);
      setDocPreview(null);
      setActiveTab('uebersicht');
      return;
    }

    if (!isOmdCatalogMaterial(material, omdEnabledSlugs)) {
      setLightbox(null);
      setDocPreview(null);
      setFetchState({
        status: 'ineligible',
        message: 'Open Master Data ist für diesen Großhändler nicht verfügbar.',
      });
      return;
    }

    const supplierPid = resolveArticleNumber(material);
    if (!supplierPid) {
      setLightbox(null);
      setDocPreview(null);
      setFetchState({
        status: 'ineligible',
        message: 'Für dieses Material ist keine Artikelnummer hinterlegt.',
      });
      return;
    }

    setLightbox(null);
    setDocPreview(null);

    const supplierSlug = material.supplier_slug || 'g-c';
    const cacheKey = getOmdProductCacheKey(instanceId, supplierSlug, supplierPid);
    const cachedProduct = getCachedOmdProduct(cacheKey);
    if (cachedProduct) {
      setFetchState({ status: 'success', product: cachedProduct });
      return;
    }

    let cancelled = false;
    setFetchState({ status: 'loading' });
    const url = `/api/integrations/omd/${encodeURIComponent(supplierSlug)}/product?${new URLSearchParams({ supplierPid }).toString()}`;

    getOrFetchOmdProduct(cacheKey, async () => {
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(String(data?.error || 'OMD-Abfrage fehlgeschlagen.')) as Error & {
          code?: string;
          settingsLink?: boolean;
        };
        err.code = typeof data?.code === 'string' ? data.code : undefined;
        err.settingsLink = data?.code === 'not_configured' || data?.code === 'auth_failed';
        throw err;
      }
      return data?.product ?? data;
    })
      .then((product) => {
        if (cancelled) return;
        setFetchState({ status: 'success', product });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof Error && 'code' in err) {
          setFetchState({
            status: 'error',
            message: err.message,
            code: typeof err.code === 'string' ? err.code : undefined,
            settingsLink: Boolean((err as Error & { settingsLink?: boolean }).settingsLink),
          });
          return;
        }
        setFetchState({ status: 'error', message: 'Netzwerkfehler bei der OMD-Abfrage.' });
      });

    return () => {
      cancelled = true;
    };
  }, [open, material, instanceId, omdEnabledSlugs]);

  const openLightbox = React.useCallback((index: number, product: unknown) => {
    const data = requireOmdProduct(product);
    if (!data) return;
    const pictures = buildLightboxPictures(data);
    if (pictures.length === 0) return;
    setLightbox({ pictures, index });
  }, []);

  const openDocument = React.useCallback((doc: OmdDocument) => {
    if (!doc.url) return;
    const preview = docPreviewFromDocument(doc);
    setDocPreview({
      kind: 'document',
      url: preview.url,
      title: preview.title,
      filename: preview.filename,
      documentType: preview.documentType,
    });
  }, []);

  if (!open || !material) return null;

  const productData =
    fetchState.status === 'success' ? requireOmdProduct(fetchState.product) : null;
  const rawProduct = fetchState.status === 'success' ? fetchState.product : null;
  const showTabs = settings.layout === 'tabs';

  const tabContent =
    productData && rawProduct && material ? (
      showTabs ? (
        <>
          {activeTab === 'uebersicht' && (
            <OmdOverviewTab
              product={productData}
              rawProduct={rawProduct}
              settings={settings}
              onOpenLightbox={(i) => openLightbox(i, rawProduct)}
            />
          )}
          {activeTab === 'technik' && (
            <OmdTechnikTab product={productData} rawProduct={rawProduct} settings={settings} />
          )}
          {activeTab === 'dokumente' && (
            <OmdDocumentsTab
              product={productData}
              rawProduct={rawProduct}
              settings={settings}
              onOpenLightbox={(i) => openLightbox(i, rawProduct)}
              onOpenDocument={openDocument}
            />
          )}
          {activeTab === 'preise' && (
            <OmdPreiseTab
              product={productData}
              rawProduct={rawProduct}
              material={material}
              settings={settings}
            />
          )}
        </>
      ) : (
        <>
          <OmdFlowSection id="uebersicht" title={TAB_LABELS.uebersicht} settings={settings}>
            <OmdOverviewTab
              product={productData}
              rawProduct={rawProduct}
              settings={settings}
              onOpenLightbox={(i) => openLightbox(i, rawProduct)}
              hideRawJson
            />
          </OmdFlowSection>
          <OmdFlowSection id="technik" title={TAB_LABELS.technik} settings={settings}>
            <OmdTechnikTab
              product={productData}
              rawProduct={rawProduct}
              settings={settings}
              hideRawJson
            />
          </OmdFlowSection>
          <OmdFlowSection id="dokumente" title={TAB_LABELS.dokumente} settings={settings}>
            <OmdDocumentsTab
              product={productData}
              rawProduct={rawProduct}
              settings={settings}
              onOpenLightbox={(i) => openLightbox(i, rawProduct)}
              onOpenDocument={openDocument}
              hideRawJson
            />
          </OmdFlowSection>
          <OmdFlowSection id="preise" title={TAB_LABELS.preise} settings={settings}>
            <OmdPreiseTab
              product={productData}
              rawProduct={rawProduct}
              material={material}
              settings={settings}
              hideRawJson
            />
          </OmdFlowSection>
          <OmdRawJsonSection product={rawProduct} />
        </>
      )
    ) : null;

  return createPortal(
    <>
      <button
        type="button"
        aria-label="Open Master Data schließen"
        className="fixed inset-0 z-[70] bg-slate-900/30"
        onClick={onClose}
      />
      <aside
        ref={panelRef}
        className="fixed inset-y-0 right-0 z-[71] w-full bg-app-surface border-l border-app-border shadow-xl flex flex-col"
        style={{ maxWidth: settings.panelWidthPx }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="omd-panel-title"
      >
        <OmdPanelHeader
          material={material}
          product={fetchState.status === 'success' ? fetchState.product : null}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onClose={onClose}
          showTabs={showTabs && fetchState.status === 'success'}
        />

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 bg-app-bg">
          {fetchState.status === 'loading' && (
            <div className="space-y-3 animate-pulse">
              <div className="h-32 rounded-lg bg-slate-200" />
              <div className="h-24 rounded-lg bg-slate-200" />
              <div className="h-40 rounded-lg bg-slate-200" />
              <InlineLoadingState label="Open Master Data wird geladen…" className="justify-start" />
            </div>
          )}

          {fetchState.status === 'ineligible' && (
            <p className="text-sm text-app-text-muted">{fetchState.message}</p>
          )}

          {fetchState.status === 'error' && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 space-y-2">
              <p>{fetchState.message}</p>
              {fetchState.settingsLink && (
                <Link
                  to={settingsPath}
                  className="inline-block text-red-800 underline font-medium"
                  onClick={onClose}
                >
                  Open Master Data in Einstellungen konfigurieren
                </Link>
              )}
            </div>
          )}

          {tabContent}
        </div>

        {fetchState.status === 'success' && <OmdPanelFooter product={fetchState.product} material={material} />}
      </aside>

      <OmdImageLightbox
        state={lightbox}
        onClose={() => setLightbox(null)}
        onChangeIndex={(index) => setLightbox((s) => (s ? { ...s, index } : s))}
      />

      <OmdMediaPreview preview={docPreview} onClose={() => setDocPreview(null)} />
    </>,
    document.body,
  );
}
